import { memo, MouseEvent, useCallback } from 'react';
import styled from 'styled-components';

import { paradigmLightBlue } from '@paradigm/design-system/src/colors';
import { useObservable } from '@paradigm/utils/src/hooks';
import { useCounterpartiesService } from '@paradigm/features/src/unified-rfqs/contexts/counterparties';
import { isLiquidityProvider } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import { counterpartyColor } from '@paradigm/features/src/unified-rfqs/ui/drfq/counterparty-color';

import UnstyledButton from '#/components/buttons/UnstyledButton';
import CheckedIcon from '@paradigm/design-system/src/assets/CheckedIcon';
import LiquidityProviderIcon from '@paradigm/design-system/src/assets/LiquidityProviderIcon';
import StarIcon from '@paradigm/design-system/src/assets/StarIcon';

import { MOBILE_BREAKPOINT } from '#/containers/layout/constants';
import PrimeBadge from '@paradigm/design-system/src/assets/PrimeBadge';
import { useTrackAction } from '@paradigm/features/src/analytics';
import { useCpsCtxMenu } from './cps-context-menu';

interface Props {
  readonly id: string;
}

const CpItem = memo(function CpItem({ id }: Props) {
  const cpsService = useCounterpartiesService();
  const cp = useObservable(cpsService.store.item(id));
  const ctxMenu = useCpsCtxMenu();
  const trackAction = useTrackAction();

  const selected = cp != null && cp.selectedAt !== 0;

  const handleClick = useCallback(() => {
    trackAction('select counterparty', { counterpartyId: id });
    cpsService.setSelected(id, !selected);
  }, [cpsService, id, selected, trackAction]);

  if (cp == null) return <Container />;

  const isLp = isLiquidityProvider(cp.data);
  const { isFavorite } = cp;

  const handleContextMenu = (event: MouseEvent) => {
    ctxMenu.handleMouseEvent(event, { counterpartyId: id });
  };

  return (
    <Container
      aria-selected={selected}
      role="option"
      data-testid={`counterparty-${id}`}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
    >
      <InitialsDisk color={counterpartyColor(cp.data.desk_name)}>
        {cp.data.firm_name.slice(0, 2).toUpperCase()}
      </InitialsDisk>
      <CpDetails>
        <FirmName>{cp.data.firm_name}</FirmName>
        <Desk>
          {isLp && <PrimeBadge />} ({cp.data.desk_name})
        </Desk>
      </CpDetails>
      <Cell>{isLp && lpIcon}</Cell>
      <Cell>{isFavorite && starIcon}</Cell>
      <Cell css="margin-left: 12px">
        {selected ? checkedIcon : uncheckedIcon}
      </Cell>
    </Container>
  );
});

export default CpItem;
const Container = styled(UnstyledButton)`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto auto auto;
  padding: 0 var(--h-pad);
  height: 60px;
  width: 100%;
  border-radius: 3px;
  text-align: left;

  :focus-visible {
    box-shadow: inset 0 0 0 1px ${paradigmLightBlue};
  }
  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr auto auto auto;
    height: 80px;
  }
`;

const InitialsDisk = styled.div<{ color: string }>`
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 100%;
  background: ${(props) => props.color};

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    display: none;
  }
`;

const CpDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  font-size: 13px;
  font-weight: 400;
  line-height: 16px;
  padding: 0 0 0 14px;
  overflow: hidden;

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1rem;
    padding: 0;
  }
`;

const FirmName = styled.span`
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow-x: hidden;

  ::-webkit-scrollbar-thumb {
    box-shadow: unset;
  }
`;

const Desk = styled.span`
  margin-top: 2px;
  color: rgba(255, 255, 255, 0.5);

  @media screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 0.8125rem;
    padding-top: 0.25rem;
  }
`;

const Cell = styled.div`
  display: grid;
  place-items: center;
`;

const lpIcon = <LiquidityProviderIcon style={{ marginLeft: 6 }} width="13px" />;

const starIcon = (
  <StarIcon style={{ marginLeft: 6 }} width="12px" color="#72adf2" />
);

const uncheckedIcon = (
  <svg width="24px" height="24px" viewBox="0 0 24 24">
    <circle
      cx={12}
      cy={12}
      r={11.5}
      stroke="rgba(255, 255, 255, 0.5)"
      fill="none"
    />
  </svg>
);

const checkedIcon = <CheckedIcon width="24px" height="24px" color="#72adf2" />;
